import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { FloatingCTA } from '../components/FloatingCTA';
import { SearchIcon, FilterIcon, MapPinIcon, CalendarIcon, TagIcon, BuildingIcon, CheckIcon, XIcon, ChevronRightIcon, ChevronLeftIcon, EyeIcon, BookmarkIcon, DownloadIcon, PlusIcon, InfoIcon, BellIcon, FileTextIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function DemoPage() {
  const [activeTab, setActiveTab] = useState('recherche');
  const [selectedCountry, setSelectedCountry] = useState('Tous les pays');
  const [selectedSector, setSelectedSector] = useState('Tous les secteurs');
  const [showModal, setShowModal] = useState(false);
  const [currentTenderIndex, setCurrentTenderIndex] = useState(0);
  const [savedTenders, setSavedTenders] = useState<number[]>([1, 3]);
  // Demo data
  const countries = ['Tous les pays', 'Maroc', 'Tunisie', 'Algérie', 'Sénégal', "Côte d'Ivoire", 'Cameroun'];
  const sectors = ['Tous les secteurs', 'BTP & Construction', 'Technologies & Télécoms', 'Énergie & Utilities', 'Santé & Pharmaceutique', 'Agriculture & Agroalimentaire', 'Transport & Logistique', 'Éducation & Formation'];
  const tenders = [{
    id: 1,
    title: "Construction d'un centre commercial à Casablanca, Maroc",
    country: 'Maroc',
    sector: 'BTP & Construction',
    budget: '4,2M €',
    deadline: '15/12/2023',
    publishDate: '20/10/2023',
    match: 92,
    description: 'Call for tenders for the construction of a 25,000 square metre commercial centre including 120 shops, a hypermarket and a dining area. The project also includes the arrangement of car parks and surrounding green spaces.'
  }, {
    id: 2,
    title: 'Fourniture et installation de matériel informatique pour universités',
    country: 'Tunisie',
    sector: 'Technologies & Télécoms',
    budget: '850K €',
    deadline: '05/12/2023',
    publishDate: '25/10/2023',
    match: 85,
    description: 'Supply, installation and commissioning of computer equipment for 5 universities. The market includes 500 computers, 100 servers, network equipment and associated software, as well as training of technical staff.'
  }, {
    id: 3,
    title: 'Installation de panneaux solaires pour bâtiments administratifs',
    country: "Côte d'Ivoire",
    sector: 'Énergie & Utilities',
    budget: '1,8M €',
    deadline: '10/01/2024',
    publishDate: '01/11/2023',
    match: 78,
    description: 'Supply and installation of photovoltaic systems for 25 administrative buildings in Abidjan. Total power: 1.2 MW. The project includes panels, converters, storage systems and performance monitoring for 2 years.'
  }, {
    id: 4,
    title: "Réhabilitation du réseau d'assainissement de Dakar - Est",
    country: 'Sénégal',
    sector: 'BTP & Construction',
    budget: '3,5M €',
    deadline: '20/12/2023',
    publishDate: '28/10/2023',
    match: 90,
    description: "Travaux de réhabilitation du réseau d'assainissement dans la zone Est de Dakar. Le projet comprend le remplacement de 15 km de canalisations, la construction de 3 stations de pompage et la rénovation de 500 regards."
  }, {
    id: 5,
    title: 'Supply of medical equipment, for regional hospitals,',
    country: 'Cameroun',
    sector: 'Santé & Pharmaceutique',
    budget: '2,1M €',
    deadline: '15/01/2024',
    publishDate: '05/11/2023',
    match: 75,
    description: 'Supply, installation and maintenance of medical equipment for 8 regional hospitals. The market includes medical imaging equipment, laboratory equipment, surgical materiel and monitoring systems.'
  }];
  const toggleSavedTender = (id: number) => {
    if (savedTenders.includes(id)) {
      setSavedTenders(savedTenders.filter(tenderId => tenderId !== id));
    } else {
      setSavedTenders([...savedTenders, id]);
    }
  };
  const filteredTenders = tenders.filter(tender => {
    if (selectedCountry !== 'Tous les pays' && tender.country !== selectedCountry) {
      return false;
    }
    if (selectedSector !== 'Tous les secteurs' && tender.sector !== selectedSector) {
      return false;
    }
    return true;
  });
  const currentTender = tenders[currentTenderIndex];
  return <>
      {' '}
      <div className="min-h-screen bg-gray-50 text-gray-900 font-sans" data-id="element-2007">
        <Navbar data-id="element-2008" />
        {/* Hero Section */}
        <section className="pt-28 pb-16 bg-white border-b border-gray-200" data-id="element-2009">
          <div className="container mx-auto px-4" data-id="element-2010">
            <div className="max-w-4xl mx-auto text-center" data-id="element-2011">
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-4" data-id="element-2012">
                Démonstration interactive
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-2013">
                Explorez notre plateforme en mode démo
              </h1>
              <p className="text-lg text-gray-600 mb-8" data-id="element-2014">
                Découvrez comment ElevateIT peut vous aider à identifier et
                saisir les meilleures opportunités d'affaires en Afrique.
                Naviguez entre les différentes fonctionnalités pour une
                expérience complète.
              </p>
            </div>
          </div>
        </section>
        {/* Demo Interface */}
        <section className="py-12" data-id="element-2015">
          <div className="container mx-auto px-4" data-id="element-2016">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-2017">
              {/* Navigation Tabs */}
              <div className="flex border-b border-gray-200" data-id="element-2018">
                {[{
                id: 'recherche',
                label: "Recherche d'appels d'offres"
              }, {
                id: 'alertes',
                label: 'Alertes intelligentes'
              }, {
                id: 'analyse',
                label: 'Analyse et rapports'
              }, {
                id: 'favoris',
                label: "Appels d'offres sauvegardés"
              }].map(tab => <button key={tab.id} className={`px-6 py-4 text-sm font-medium transition-colors ${activeTab === tab.id ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} onClick={() => setActiveTab(tab.id)} data-id="element-2019">
                    {tab.label}
                  </button>)}
              </div>
              {/* Content Area */}
              <div className="p-6" data-id="element-2020">
                {activeTab === 'recherche' && <div data-id="element-2021">
                    <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-2022">
                      {/* Search Bar */}
                      <div className="flex-1 relative" data-id="element-2023">
                        <input type="text" placeholder="Rechercher par mot-clé, référence..." className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2024" />
                        <SearchIcon className="absolute left-3 top-3.5 text-gray-400" size={18} data-id="element-2025" />
                      </div>
                      {/* Filters */}
                      <div className="flex gap-3" data-id="element-2026">
                        <select value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)} className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" data-id="element-2027">
                          {countries.map(country => <option key={country} value={country} data-id="element-2028">
                              {country}
                            </option>)}
                        </select>
                        <select value={selectedSector} onChange={e => setSelectedSector(e.target.value)} className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" data-id="element-2029">
                          {sectors.map(sector => <option key={sector} value={sector} data-id="element-2030">
                              {sector}
                            </option>)}
                        </select>
                        <button className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg flex items-center hover:bg-gray-200 transition-colors" data-id="element-2031">
                          <FilterIcon size={18} className="mr-2" data-id="element-2032" />
                          <span className="hidden md:inline" data-id="element-2033">
                            Plus de filtres
                          </span>
                        </button>
                      </div>
                    </div>
                    {/* Results */}
                    <div className="mb-4 text-sm text-gray-500" data-id="element-2034">
                      {filteredTenders.length} résultats trouvés
                    </div>
                    <div className="space-y-4" data-id="element-2035">
                      {filteredTenders.map(tender => <div key={tender.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer" onClick={() => {
                    setCurrentTenderIndex(tenders.findIndex(t => t.id === tender.id));
                    setShowModal(true);
                  }} data-id="element-2036">
                          <div className="flex justify-between" data-id="element-2037">
                            <div data-id="element-2038">
                              <div className="text-lg font-medium text-blue-800 mb-2" data-id="element-2039">
                                {tender.title}
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-2 gap-x-4 text-sm" data-id="element-2040">
                                <div className="flex items-center" data-id="element-2041">
                                  <MapPinIcon size={16} className="text-gray-400 mr-2" data-id="element-2042" />
                                  <span data-id="element-2043">{tender.country}</span>
                                </div>
                                <div className="flex items-center" data-id="element-2044">
                                  <BuildingIcon size={16} className="text-gray-400 mr-2" data-id="element-2045" />
                                  <span data-id="element-2046">{tender.sector}</span>
                                </div>
                                <div className="flex items-center" data-id="element-2047">
                                  <TagIcon size={16} className="text-gray-400 mr-2" data-id="element-2048" />
                                  <span data-id="element-2049">{tender.budget}</span>
                                </div>
                                <div className="flex items-center" data-id="element-2050">
                                  <CalendarIcon size={16} className="text-gray-400 mr-2" data-id="element-2051" />
                                  <span data-id="element-2052">Expire le {tender.deadline}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end" data-id="element-2053">
                              <div className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full mb-2" data-id="element-2054">
                                Correspondance: {tender.match}%
                              </div>
                              <button onClick={e => {
                          e.stopPropagation();
                          toggleSavedTender(tender.id);
                        }} className="text-gray-400 hover:text-yellow-500 transition-colors" data-id="element-2055">
                                <BookmarkIcon size={20} fill={savedTenders.includes(tender.id) ? 'currentColor' : 'none'} className={savedTenders.includes(tender.id) ? 'text-yellow-500' : ''} data-id="element-2056" />
                              </button>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>}
                {activeTab === 'alertes' && <div data-id="element-2057">
                    <div className="flex justify-between items-center mb-6" data-id="element-2058">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-2059">
                        Mes alertes
                      </h2>
                      <Button variant="primary" size="sm" className="flex items-center" data-id="element-2060">
                        <PlusIcon size={16} className="mr-1" data-id="element-2061" />
                        Nouvelle alerte
                      </Button>
                    </div>
                    <div className="space-y-4" data-id="element-2062">
                      {[{
                    name: 'Projets BTP au Maroc',
                    criteria: 'Sector: BTP & Construction, Country: Morocco',
                    count: 12,
                    lastUpdate: 'il y a 2 heures'
                  }, {
                    name: 'Technologies en Tunisie et Algérie',
                    criteria: 'Sector: Technologies & Telecommunications, Country: Tunisia, Algeria',
                    count: 8,
                    lastUpdate: 'il y a 1 jour'
                  }, {
                    name: "Projets énergétiques en Afrique de l'Ouest",
                    criteria: "Sector: Energy & Utilities, Country: Senegal, Côte d'Ivoire",
                    count: 5,
                    lastUpdate: 'il y a 3 jours'
                  }].map((alert, index) => <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all" data-id="element-2063">
                          <div className="flex justify-between items-start" data-id="element-2064">
                            <div data-id="element-2065">
                              <div className="text-lg font-medium text-blue-800 mb-1" data-id="element-2066">
                                {alert.name}
                              </div>
                              <div className="text-sm text-gray-600 mb-2" data-id="element-2067">
                                {alert.criteria}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-2068">
                                Dernière mise à jour: {alert.lastUpdate}
                              </div>
                            </div>
                            <div className="flex flex-col items-end" data-id="element-2069">
                              <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full mb-2" data-id="element-2070">
                                {alert.count} nouveaux résultats
                              </div>
                              <div className="flex space-x-2" data-id="element-2071">
                                <button className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2072">
                                  <EyeIcon size={18} data-id="element-2073" />
                                </button>
                                <button className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2074">
                                  <InfoIcon size={18} data-id="element-2075" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>)}
                    </div>
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-2076">
                      <div className="flex items-start" data-id="element-2077">
                        <div className="bg-blue-100 rounded-full p-2 mr-3" data-id="element-2078">
                          <BellIcon size={20} className="text-blue-600" data-id="element-2079" />
                        </div>
                        <div data-id="element-2080">
                          <h3 className="font-medium text-blue-800 mb-1" data-id="element-2081">
                            Configurez vos alertes intelligentes
                          </h3>
                          <p className="text-sm text-blue-700" data-id="element-2082">
                            Définissez vos critères une seule fois et recevez
                            automatiquement les nouveaux appels d'offres
                            correspondant à votre profil.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}
                {activeTab === 'analyse' && <div data-id="element-2083">
                    <div className="flex justify-between items-center mb-6" data-id="element-2084">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-2085">
                        Analyse et rapports
                      </h2>
                      <div className="flex space-x-3" data-id="element-2086">
                        <select className="text-sm border border-gray-300 rounded-md px-3 py-2" data-id="element-2087">
                          <option data-id="element-2088">Dernier trimestre</option>
                          <option data-id="element-2089">6 derniers mois</option>
                          <option data-id="element-2090">Cette année</option>
                        </select>
                        <Button variant="outline" size="sm" className="flex items-center" data-id="element-2091">
                          <DownloadIcon size={16} className="mr-1" data-id="element-2092" />
                          Exporter
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2093">
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-2094">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-2095">
                          Appels d'offres identifiés
                        </div>
                        <div className="text-2xl font-bold text-gray-900" data-id="element-2096">
                          1,248
                        </div>
                        <div className="text-xs text-green-600 flex items-center mt-1" data-id="element-2097">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor" data-id="element-2098">
                            <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" data-id="element-2099" />
                          </svg>
                          +12.5% vs période précédente
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-2100">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-2101">
                          Taux de correspondance
                        </div>
                        <div className="text-2xl font-bold text-gray-900" data-id="element-2102">
                          76%
                        </div>
                        <div className="text-xs text-green-600 flex items-center mt-1" data-id="element-2103">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor" data-id="element-2104">
                            <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" data-id="element-2105" />
                          </svg>
                          +4.2% vs période précédente
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-2106">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-2107">
                          Montant moyen
                        </div>
                        <div className="text-2xl font-bold text-gray-900" data-id="element-2108">
                          320K€
                        </div>
                        <div className="text-xs text-green-600 flex items-center mt-1" data-id="element-2109">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor" data-id="element-2110">
                            <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" data-id="element-2111" />
                          </svg>
                          +14.3% vs période précédente
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2112">
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-2113">
                        <div className="flex justify-between items-center mb-4" data-id="element-2114">
                          <h3 className="font-medium text-gray-900" data-id="element-2115">
                            Répartition par secteur
                          </h3>
                        </div>
                        <div className="h-64 flex items-end space-x-4 justify-center" data-id="element-2116">
                          <div className="flex flex-col items-center" data-id="element-2117">
                            <div className="w-16 bg-blue-500 rounded-t-sm" style={{
                          height: '60%'
                        }} data-id="element-2118"></div>
                            <div className="text-xs mt-1" data-id="element-2119">BTP</div>
                          </div>
                          <div className="flex flex-col items-center" data-id="element-2120">
                            <div className="w-16 bg-blue-400 rounded-t-sm" style={{
                          height: '85%'
                        }} data-id="element-2121"></div>
                            <div className="text-xs mt-1" data-id="element-2122">IT</div>
                          </div>
                          <div className="flex flex-col items-center" data-id="element-2123">
                            <div className="w-16 bg-blue-300 rounded-t-sm" style={{
                          height: '45%'
                        }} data-id="element-2124"></div>
                            <div className="text-xs mt-1" data-id="element-2125">Santé</div>
                          </div>
                          <div className="flex flex-col items-center" data-id="element-2126">
                            <div className="w-16 bg-blue-200 rounded-t-sm" style={{
                          height: '30%'
                        }} data-id="element-2127"></div>
                            <div className="text-xs mt-1" data-id="element-2128">Énergie</div>
                          </div>
                          <div className="flex flex-col items-center" data-id="element-2129">
                            <div className="w-16 bg-blue-100 rounded-t-sm" style={{
                          height: '20%'
                        }} data-id="element-2130"></div>
                            <div className="text-xs mt-1" data-id="element-2131">Autres</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-2132">
                        <div className="flex justify-between items-center mb-4" data-id="element-2133">
                          <h3 className="font-medium text-gray-900" data-id="element-2134">
                            Répartition géographique
                          </h3>
                        </div>
                        <div className="h-64 p-4 flex items-center justify-center bg-gray-50 rounded-lg" data-id="element-2135">
                          <div className="text-center text-gray-500" data-id="element-2136">
                            <div className="mb-2 text-lg font-medium" data-id="element-2137">
                              Carte interactive
                            </div>
                            <p className="text-sm" data-id="element-2138">
                              Visualisation de la répartition des appels
                              d'offres par pays
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}
                {activeTab === 'favoris' && <div data-id="element-2139">
                    <div className="flex justify-between items-center mb-6" data-id="element-2140">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-2141">
                        Appels d'offres sauvegardés
                      </h2>
                      <div className="text-sm text-gray-500" data-id="element-2142">
                        {savedTenders.length} élément
                        {savedTenders.length > 1 ? 's' : ''}
                      </div>
                    </div>
                    {savedTenders.length > 0 ? <div className="space-y-4" data-id="element-2143">
                        {tenders.filter(tender => savedTenders.includes(tender.id)).map(tender => <div key={tender.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer" onClick={() => {
                    setCurrentTenderIndex(tenders.findIndex(t => t.id === tender.id));
                    setShowModal(true);
                  }} data-id="element-2144">
                              <div className="flex justify-between" data-id="element-2145">
                                <div data-id="element-2146">
                                  <div className="text-lg font-medium text-blue-800 mb-2" data-id="element-2147">
                                    {tender.title}
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-y-2 gap-x-4 text-sm" data-id="element-2148">
                                    <div className="flex items-center" data-id="element-2149">
                                      <MapPinIcon size={16} className="text-gray-400 mr-2" data-id="element-2150" />
                                      <span data-id="element-2151">{tender.country}</span>
                                    </div>
                                    <div className="flex items-center" data-id="element-2152">
                                      <BuildingIcon size={16} className="text-gray-400 mr-2" data-id="element-2153" />
                                      <span data-id="element-2154">{tender.sector}</span>
                                    </div>
                                    <div className="flex items-center" data-id="element-2155">
                                      <TagIcon size={16} className="text-gray-400 mr-2" data-id="element-2156" />
                                      <span data-id="element-2157">{tender.budget}</span>
                                    </div>
                                    <div className="flex items-center" data-id="element-2158">
                                      <CalendarIcon size={16} className="text-gray-400 mr-2" data-id="element-2159" />
                                      <span data-id="element-2160">Expire le {tender.deadline}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end" data-id="element-2161">
                                  <div className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full mb-2" data-id="element-2162">
                                    Correspondance: {tender.match}%
                                  </div>
                                  <button onClick={e => {
                          e.stopPropagation();
                          toggleSavedTender(tender.id);
                        }} className="text-yellow-500 hover:text-gray-400 transition-colors" data-id="element-2163">
                                    <BookmarkIcon size={20} fill="currentColor" data-id="element-2164" />
                                  </button>
                                </div>
                              </div>
                            </div>)}
                      </div> : <div className="text-center py-12" data-id="element-2165">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4" data-id="element-2166">
                          <BookmarkIcon size={24} className="text-gray-400" data-id="element-2167" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2168">
                          Aucun appel d'offres sauvegardé
                        </h3>
                        <p className="text-gray-500 mb-6" data-id="element-2169">
                          Marquez vos appels d'offres préférés pour les
                          retrouver facilement
                        </p>
                        <Button variant="outline" size="sm" onClick={() => setActiveTab('recherche')} data-id="element-2170">
                          Rechercher des appels d'offres
                        </Button>
                      </div>}
                  </div>}
              </div>
            </div>
            <div className="mt-8 text-center" data-id="element-2171">
              <p className="text-gray-600 mb-6" data-id="element-2172">
                Ceci est une démonstration interactive. Pour accéder à toutes
                les fonctionnalités, créez un compte gratuit.
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-2173">
                <Button variant="primary" size="lg" className="shadow-lg shadow-blue-500/20" onClick={() => window.location.href = '/essai-gratuit'} data-id="element-2174">
                  Essayer gratuitement
                </Button>
                <Button variant="outline" size="lg" className="border-2 border-blue-900" onClick={() => window.location.href = '/#pricing'} data-id="element-2175">
                  Voir les tarifs
                </Button>
              </div>
            </div>
          </div>
        </section>
        {/* Tender Detail Modal */}
        {showModal && currentTender && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-2176">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" data-id="element-2177">
              <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-2178">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-2179">
                  Détails de l'appel d'offres
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-2180">
                  <XIcon size={20} data-id="element-2181" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]" data-id="element-2182">
                <div className="flex justify-between items-start mb-6" data-id="element-2183">
                  <div data-id="element-2184">
                    <h3 className="text-2xl font-bold text-blue-900 mb-2" data-id="element-2185">
                      {currentTender.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-500" data-id="element-2186">
                      <span data-id="element-2187">Publié le {currentTender.publishDate}</span>
                      <span className="mx-2" data-id="element-2188">•</span>
                      <span data-id="element-2189">Référence: AO-{currentTender.id}25478</span>
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-2190">
                    <button onClick={() => toggleSavedTender(currentTender.id)} className={`p-2 rounded-full ${savedTenders.includes(currentTender.id) ? 'bg-yellow-50 text-yellow-500' : 'bg-gray-50 text-gray-400 hover:text-gray-600'}`} data-id="element-2191">
                      <BookmarkIcon size={20} fill={savedTenders.includes(currentTender.id) ? 'currentColor' : 'none'} data-id="element-2192" />
                    </button>
                    <button className="p-2 bg-gray-50 rounded-full text-gray-400 hover:text-gray-600" data-id="element-2193">
                      <DownloadIcon size={20} data-id="element-2194" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2195">
                  <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2196">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-2197">Pays</div>
                    <div className="font-medium flex items-center" data-id="element-2198">
                      <MapPinIcon size={16} className="text-blue-500 mr-2" data-id="element-2199" />
                      {currentTender.country}
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2200">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-2201">Secteur</div>
                    <div className="font-medium flex items-center" data-id="element-2202">
                      <BuildingIcon size={16} className="text-blue-500 mr-2" data-id="element-2203" />
                      {currentTender.sector}
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2204">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-2205">
                      Budget estimé
                    </div>
                    <div className="font-medium flex items-center" data-id="element-2206">
                      <TagIcon size={16} className="text-blue-500 mr-2" data-id="element-2207" />
                      {currentTender.budget}
                    </div>
                  </div>
                </div>
                <div className="mb-8" data-id="element-2208">
                  <h4 className="text-lg font-bold text-gray-900 mb-3" data-id="element-2209">
                    Description
                  </h4>
                  <p className="text-gray-700 mb-4" data-id="element-2210">
                    {currentTender.description}
                  </p>
                  <p className="text-gray-700" data-id="element-2211">
                    Le marché comprend les études d'exécution, les travaux de
                    préparation du site, les travaux de fondation, la
                    construction du gros œuvre, les travaux de second œuvre, les
                    aménagements intérieurs et extérieurs, ainsi que les
                    installations techniques (électricité, plomberie, CVC,
                    sécurité incendie, etc.).
                  </p>
                </div>
                <div className="mb-8" data-id="element-2212">
                  <h4 className="text-lg font-bold text-gray-900 mb-3" data-id="element-2213">
                    Documents
                  </h4>
                  <div className="space-y-2" data-id="element-2214">
                    {['Cahier des charges.pdf', 'Plans architecturaux.zip', 'Bordereau des prix.xlsx', 'Règlement de consultation.pdf'].map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-2215">
                        <div className="flex items-center" data-id="element-2216">
                          <FileTextIcon size={18} className="text-gray-400 mr-2" data-id="element-2217" />
                          <span className="text-gray-700" data-id="element-2218">{doc}</span>
                        </div>
                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-2219">
                          Télécharger
                        </button>
                      </div>)}
                  </div>
                </div>
                <div data-id="element-2220">
                  <h4 className="text-lg font-bold text-gray-900 mb-3" data-id="element-2221">
                    Dates importantes
                  </h4>
                  <div className="space-y-4" data-id="element-2222">
                    <div className="flex items-center" data-id="element-2223">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-2224">
                        <CheckIcon size={16} className="text-green-600" data-id="element-2225" />
                      </div>
                      <div data-id="element-2226">
                        <div className="text-sm font-medium" data-id="element-2227">
                          Date de publication
                        </div>
                        <div className="text-gray-500" data-id="element-2228">
                          {currentTender.publishDate}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-2229">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3" data-id="element-2230">
                        <CalendarIcon size={16} className="text-blue-600" data-id="element-2231" />
                      </div>
                      <div data-id="element-2232">
                        <div className="text-sm font-medium" data-id="element-2233">
                          Visite du site (optionnelle)
                        </div>
                        <div className="text-gray-500" data-id="element-2234">05/12/2023</div>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-2235">
                      <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center mr-3" data-id="element-2236">
                        <CalendarIcon size={16} className="text-yellow-600" data-id="element-2237" />
                      </div>
                      <div data-id="element-2238">
                        <div className="text-sm font-medium" data-id="element-2239">
                          Date limite de soumission
                        </div>
                        <div className="text-gray-500" data-id="element-2240">
                          {currentTender.deadline}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-between" data-id="element-2241">
                <div className="flex items-center" data-id="element-2242">
                  <div className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full" data-id="element-2243">
                    Correspondance: {currentTender.match}%
                  </div>
                </div>
                <div className="flex space-x-3" data-id="element-2244">
                  <Button variant="outline" onClick={() => setShowModal(false)} data-id="element-2245">
                    Fermer
                  </Button>
                  <Button variant="primary" data-id="element-2246">Exprimer mon intérêt</Button>
                </div>
              </div>
            </div>
          </div>}
        <Footer data-id="element-2247" />
        <FloatingCTA data-id="element-2248" />
      </div>{' '}
    </>;
}